<template>
  <nav
    :class="['side-menu', { 'side-menu--collapsed': isCollapsed }]"
    :aria-label="t('accessibility.primaryNavigation')"
  >
    <div class="side-menu__top">
      <div class="side-menu__logo">
        <img
          src="@/assets/Main Logo.png"
          :alt="t('accessibility.scrivioLogo')"
          class="side-menu__logo-image"
        />
        <img
          src="@/assets/Icon 01.png"
          :alt="t('accessibility.scrivioLogo')"
          class="side-menu__logo-mark"
        />
      </div>

      <div class="side-menu__org-switcher">
        <OrganizationSwitcher :collapsed="isCollapsed" />
      </div>

      <!-- The one part of the sidebar that gives when the viewport is short.
           `__bottom` is deliberately outside it, so the feedback button and the
           collapse toggle stay on screen however long the nav grows (#4372). The
           logo and the org switcher stay outside too — the switcher's dropdown is
           absolutely positioned and wider than the collapsed rail, so a scroll
           container around it would clip it. -->
      <div class="side-menu__scroll">
        <!-- Global nav. Inside a context — a client dossier or Organisatiebeheer —
             it becomes a collapsible "Hoofdmenu" group (chevron). What it starts
             as is `defaultHoofdmenuCollapsed` below: collapsed, so the context block
             takes focus, except in a dossier on the expanded sidebar. Outside a
             context it renders flat, exactly as before. -->
        <button
          v-if="isContextOpen && !isCollapsed"
          class="side-menu__group-header"
          :aria-expanded="!isHoofdmenuCollapsed"
          data-testid="side-menu-hoofdmenu-toggle"
          @click="toggleHoofdmenu"
        >
          <span class="side-menu__group-title">{{ t('navigation.hoofdmenu') }}</span>
          <ChevronDown
            class="side-menu__group-chevron"
            :class="{ 'side-menu__group-chevron--collapsed': isHoofdmenuCollapsed }"
            :size="18"
          />
        </button>

        <div
          v-show="!(isContextOpen && !isCollapsed && isHoofdmenuCollapsed)"
          class="side-menu__nav">
          <router-link
            v-if="!isFormsOnly"
            to="/dashboard"
            class="side-menu__item"
            active-class="side-menu__item--active"
            :title="isCollapsed ? t('navigation.dashboard') : undefined"
            @click="handleNavigation"
          >
            <LayoutDashboard class="side-menu__icon" :size="20" />
            <span class="side-menu__label">{{ t('navigation.dashboard') }}</span>
          </router-link>

          <router-link
            v-if="hasOrganization && !isFormsOnly"
            to="/clients"
            class="side-menu__item"
            active-class="side-menu__item--active"
            :title="isCollapsed ? t('navigation.clients') : undefined"
            @click="handleNavigation"
          >
            <Users class="side-menu__icon" :size="20" />
            <span class="side-menu__label">{{ t('navigation.clients') }}</span>
          </router-link>

          <router-link
            v-if="hasOrganization && !isFormsOnly && hasPermission(Permission.CLIENT_GROUPS_READ)"
            to="/client-groups"
            class="side-menu__item"
            active-class="side-menu__item--active"
            :title="isCollapsed ? t('navigation.groups') : undefined"
            @click="handleNavigation"
          >
            <Layers class="side-menu__icon" :size="20" />
            <span class="side-menu__label">{{ t('navigation.groups') }}</span>
          </router-link>

          <router-link
            v-if="hasOrganization && !isFormsOnly"
            to="/agenda"
            class="side-menu__item"
            active-class="side-menu__item--active"
            :title="isCollapsed ? t('navigation.agenda') : undefined"
            @click="handleNavigation"
          >
            <CalendarDays class="side-menu__icon" :size="20" />
            <span class="side-menu__label">{{ t('navigation.agenda') }}</span>
          </router-link>

          <router-link
            v-if="showFormsNav"
            to="/form-submissions"
            class="side-menu__item"
            active-class="side-menu__item--active"
            :title="isCollapsed ? t('navigation.forms') : undefined"
            @click="handleNavigation"
          >
            <ClipboardList class="side-menu__icon" :size="20" />
            <span class="side-menu__label">{{ t('navigation.forms') }}</span>
          </router-link>

          <router-link
            v-if="hasOrganization && !isFormsOnly && hasPermission(Permission.BILLING_READ)"
            to="/claims"
            class="side-menu__item"
            active-class="side-menu__item--active"
            :title="isCollapsed ? t('navigation.billing') : undefined"
            @click="handleNavigation"
          >
            <Euro class="side-menu__icon" :size="20" />
            <span class="side-menu__label">{{ t('navigation.billing') }}</span>
          </router-link>

          <!-- Berichten (#4665): only for an organisation with Jeugdwet enabled (#4697). -->
          <router-link
            v-if="showBerichten"
            :to="{ name: 'berichten' }"
            class="side-menu__item"
            active-class="side-menu__item--active"
            :title="isCollapsed ? t('jeugdwet.berichten.title') : undefined"
            data-testid="side-menu-berichten"
            @click="handleNavigation"
          >
            <Inbox class="side-menu__icon" :size="20" />
            <span class="side-menu__label">{{ t('jeugdwet.berichten.title') }}</span>
          </router-link>

        </div>

        <!-- Client-context block: rendered inside a client dossier. Mini client
             header + the dossier nav that switches the view.

             The test ids still say `classic` — the word distinguished this from
             the redesign's sidebar until #4501 retired it. They are kept because
             the e2e suite selects on them and there is nothing to distinguish
             them from any more. -->
        <div
          v-if="isDossierOpen && dossierClient"
          class="side-menu__client-context"
          data-testid="dossier-classic-sidebar"
        >
          <div class="side-menu__client-header">
            <ClientAvatar
              :client-id="dossierClient.id"
              :avatar-url="dossierClient.avatarUrl"
              :name="dossierClientName"
              size="sm"
            />
            <div class="side-menu__client-heading">
              <span class="side-menu__client-name">{{ dossierClientName }}</span>
              <span class="side-menu__client-subtitle">{{ t('clientDossier.nav.contextLabel') }}</span>
            </div>
          </div>

          <div class="side-menu__client-nav" :aria-label="t('clientDossier.nav.label')">
            <router-link
              v-for="item in navItems"
              :key="item.key"
              :to="{ name: item.routeName, params: { clientId: dossierClient.id, ...item.params } }"
              class="side-menu__item side-menu__item--context"
              :class="{ 'side-menu__item--active': item.key === activeView }"
              :title="isCollapsed ? t(item.labelKey) : undefined"
              :data-testid="`dossier-classic-nav-${item.key}`"
              @click="handleNavigation"
            >
              <component :is="item.icon" class="side-menu__icon" :size="20" />
              <span class="side-menu__label">{{ t(item.labelKey) }}</span>
            </router-link>
          </div>
        </div>

        <!-- Settings-context block (#4368): on an Organisatiebeheer screen, the
             same five groups the hub renders, from the same declaration. Desktop
             only — below `--breakpoint-lg-up` it is hidden and the hub stays the
             navigation, so the mobile drawer is exactly what it was. -->
        <div
          v-if="isSettingsOpen"
          class="side-menu__settings-context"
          data-testid="settings-sidebar"
        >
          <div class="side-menu__context-header">
            <Settings class="side-menu__icon" :size="20" />
            <span class="side-menu__label side-menu__context-title">
              {{ t('breadcrumbs.organizationManagement') }}
            </span>
          </div>

          <div
            v-for="group in settingsGroups"
            :key="group.id"
            class="side-menu__settings-group"
            role="group"
            :aria-label="t(group.titleKey)"
          >
            <span class="side-menu__settings-group-title">{{ t(group.titleKey) }}</span>
            <router-link
              v-for="area in group.areas"
              :key="area.id"
              :to="{ name: area.routeName }"
              class="side-menu__item side-menu__item--context"
              :class="{ 'side-menu__item--active': area.id === activeAreaId }"
              :aria-current="area.id === activeAreaId ? 'page' : undefined"
              :title="isCollapsed ? t(area.titleKey) : undefined"
              :data-testid="`settings-nav-${area.id}`"
              @click="handleNavigation"
            >
              <component :is="area.icon" class="side-menu__icon" :size="20" />
              <span class="side-menu__label">{{ t(area.titleKey) }}</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="side-menu__bottom">
      <!-- Profile, organization and logout moved to the top-right header
           (UserMenu) as part of the UI refresh (#2749). -->

      <!-- Feedback. Carries no @click on purpose: Sentry's own `attachTo` owns
           the listener, so the dialog stays the SDK's (#4061). It is left out
           entirely when Sentry is not initialised — a button that does nothing
           is worse than no button. -->
      <button
        v-if="isFeedbackAvailable"
        ref="feedbackTrigger"
        type="button"
        class="side-menu__item side-menu__item--button"
        :title="isCollapsed ? feedbackLabel : undefined"
        :aria-label="feedbackLabel"
        data-testid="side-menu-feedback"
      >
        <MessageSquare class="side-menu__icon" :size="20" />
        <span class="side-menu__label">{{ feedbackLabel }}</span>
      </button>

      <button
        class="side-menu__item side-menu__item--button side-menu__collapse-toggle"
        :title="isCollapsed ? t('navigation.expandSidebar') : t('navigation.collapseSidebar')"
        data-testid="side-menu-collapse-toggle"
        @click="toggle"
      >
        <PanelLeftOpen v-if="isCollapsed" class="side-menu__icon" :size="20" />
        <PanelLeftClose v-else class="side-menu__icon" :size="20" />
        <span class="side-menu__label">{{ t('navigation.collapseSidebar') }}</span>
      </button>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserProfileStore } from '@/stores/userProfile'
import { useOrganizationStore } from '@/stores/organization'
import { useFormsStore } from '@/stores/forms'
import { usePermissions } from '@/composables/usePermissions'
import { useSidebarState } from '@/composables/useSidebarState'
import { useFeatureAccess } from '@/composables/useFeatureAccess'
import { useDossierContext } from '@/composables/useDossierContext'
import { useOrganizationSettingsNav } from '@/composables/useOrganizationAreas'
import { useSentryFeedbackTrigger } from '@/composables/useSentryFeedback'
import { getFullName } from '@/utils/userFormatting'
import { Permission } from '@/utils/permissionBitmask'
import {
  LayoutDashboard, Users, Layers, CalendarDays, ClipboardList, Euro,
  PanelLeftClose, PanelLeftOpen, ChevronDown, MessageSquare, Settings, Inbox
} from 'lucide-vue-next'
import OrganizationSwitcher from '@/components/molecules/OrganizationSwitcher.vue'
import ClientAvatar from '@/components/molecules/ClientAvatar.vue'
import { logUnexpectedFailure } from '@/utils/errorHandling'
import { useJeugdwetEnablement } from '@/composables/jeugdwet/useJeugdwet'

const { t } = useI18n()

const userProfileStore = useUserProfileStore()
const organizationStore = useOrganizationStore()
const formsStore = useFormsStore()
const { hasPermission } = usePermissions()
const { isCollapsed, toggle, isHoofdmenuCollapsed, toggleHoofdmenu, setHoofdmenuCollapsed } = useSidebarState()
const { isFormsOnly } = useFeatureAccess()

// The Sentry feedback trigger moved here out of the bottom-right corner, where it
// covered the spot a behandelaar drags an afspraak from in the agenda (#4061).
const feedbackTrigger = ref<HTMLElement | null>(null)
const { isAvailable: isFeedbackAvailable } = useSentryFeedbackTrigger(feedbackTrigger)
// Same label the widget carried, so the dialog is announced by the name it always had.
const feedbackLabel = computed(() => t('sentry.feedback.triggerLabel'))

// Client dossier (#4256): when a dossier is open the sidebar gains a
// client-context block and the global nav becomes a collapsible "Hoofdmenu"
// group. Outside a dossier the sidebar renders exactly as before.
const { isActive: isDossierOpen, client: dossierClient, activeView, navItems } = useDossierContext()

// Organisatiebeheer (#4368): the same block one context over. Reached from the
// top-right header, so before this every move between two settings screens was a
// round trip through the hub. The groups are the hub's own — one declaration,
// one permission filter, in `useOrganizationAreas`.
const { isActive: isSettingsOpen, activeAreaId, groups: settingsGroups } = useOrganizationSettingsNav()

const dossierClientName = computed(() =>
  dossierClient.value ? getFullName(dossierClient.value) : ''
)

type SidebarContext = 'dossier' | 'settings' | null

// Which context owns the sidebar — a client dossier, Organisatiebeheer, or
// neither. Still one derived value rather than a watch per context (#4380):
// two watches would flip the hoofdmenu open and shut on a move straight from a
// dossier into Organisatiebeheer, where this makes exactly one assignment.
const sidebarContext = computed<SidebarContext>(() =>
  isDossierOpen.value ? 'dossier' : isSettingsOpen.value ? 'settings' : null
)

// Whether *a* context owns the sidebar. Everything the template asks — the
// group header, the collapsible nav — asks only this, never which of the two.
const isContextOpen = computed(() => sidebarContext.value !== null)

// What the hoofdmenu's default is under each context. Collapsed while a context
// owns the sidebar, so its own nav takes focus — except in a dossier on the
// expanded sidebar, where the column has room for both and starting collapsed
// cost a behandelaar moving around during an afspraak a click per dossier
// (#4568). Organisatiebeheer keeps the collapsed default, and so does the slim
// rail: there the value decides nothing on screen at all, since the rail hides
// the chevron and shows the global items whatever it says.
//
// `isCollapsed` is read here rather than watched, deliberately. The hoofdmenu
// state is a per-context default and stays one: a rail toggle is not a context
// change, so it neither re-derives the default nor overrules a chevron click.
function defaultHoofdmenuCollapsed(context: SidebarContext): boolean {
  if (context === null) return false
  const roomForBoth = context === 'dossier' && !isCollapsed.value
  return !roomForBoth
}

// Apply that default on entering a context, on moving to another one, and on
// leaving for a screen no context owns. `immediate` is what makes a direct URL
// into a dossier or a settings screen land on its default rather than reach it a
// moment later. Within a context the user can still toggle it by hand, and that
// choice stands until the context changes.
watch(sidebarContext, (context) => setHoofdmenuCollapsed(defaultHoofdmenuCollapsed(context)), {
  immediate: true
})

// Check if user has an organization
const hasOrganization = computed(() => {
  return userProfileStore.profile?.organizationId != null
})

// Only show the Formulieren item when the organization actually uses formulieren
// (has at least one form). FORMS_ONLY orgs use forms by definition — it is their
// primary navigation item — so they always see it (#3020).
const showFormsNav = computed(() =>
  hasOrganization.value &&
  hasPermission(Permission.FORMS_EDIT) &&
  (isFormsOnly.value || formsStore.usesForms === true)
)

// The iJw berichten are Jeugdwet's alone, so the menu offers them where the organisation
// has Jeugdwet enabled (#4697) and nowhere else.
const { enabled: jeugdwet } = useJeugdwetEnablement()
const showBerichten = computed(() => jeugdwet.value.jeugdwet)

watch(hasOrganization, async (hasOrg) => {
  if (hasOrg) {
    if (hasPermission(Permission.FORMS_EDIT) && formsStore.usesForms === null) {
      formsStore.fetchFormsUsage()
    }
    try {
      await organizationStore.fetchOrganization()
    } catch (error) {
      logUnexpectedFailure('Failed to fetch organization:', error)
    }
  }
}, { immediate: true })

const emit = defineEmits<{
  navigate: []
}>()

function handleNavigation() {
  emit('navigate')
}
</script>

<style scoped>
.side-menu {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: var(--sidebar-width-expanded);
  height: 100vh;
  background: linear-gradient(180deg, var(--color-brand-primary) 0%, var(--color-brand-primary-dark) 100%);
  color: white;
  padding: var(--spacing-lg);
  box-shadow: var(--shadow-lg);
  transition: width var(--transition-duration-moderate) var(--transition-easing-ease-out);
}

.side-menu--collapsed {
  width: var(--sidebar-width-collapsed);
  padding: var(--spacing-md);
}

.side-menu__top {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xl);
  /* Takes the whole column above `__bottom` and is allowed to shrink below its
     content: a flex item defaults to `min-height: auto` and refuses to, which is
     what pushed `__bottom` off a laptop screen with nothing to scroll (#4372). */
  flex: 1;
  min-height: 0;
}

/* The scrolling middle, and the reason there is one: a context block adds seven
   items (dossier) or twenty rows (settings) to the six the global nav already
   has, which is past the height of a laptop screen, and `.side-menu` is a fixed
   100vh column — without `min-height: 0` + `overflow-y` the overflow is simply
   unreachable. Same gap as `__top` had between these children, so the spacing is
   unchanged; it is the overflow that is new. */
.side-menu__scroll {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xl);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* The default scrollbar is drawn for a light page; this one sits on the brand
     gradient. Same overlay the sidebar's own dividers use. */
  scrollbar-width: thin;
  scrollbar-color: var(--color-overlay-on-dark) transparent;
}

/* A classic (non-overlay) scrollbar takes its width from the items, and an
   icon-only rail has none to give: 68px less the sidebar's own padding leaves an
   item exactly one icon wide, so a scrollbar there clips the icon it is supposed
   to help you reach. The rail scrolls by wheel, touch and keyboard regardless,
   and the toggle that widens it — where the scrollbar does fit — is now always on
   screen, which is the whole point of #4372.

   Stated at `lg-up`, because below that `--collapsed` is a full-width drawer
   rather than a rail (see the `lg-down` block). */
@media (--breakpoint-lg-up) {
  .side-menu--collapsed .side-menu__scroll {
    scrollbar-width: none;
  }
}

.side-menu__logo {
  padding: var(--spacing-md) 0;
  border-bottom: 1px solid var(--color-overlay-on-dark);
  overflow: hidden;
  transition: padding var(--transition-duration-moderate) var(--transition-easing-ease-out);
}

.side-menu--collapsed .side-menu__logo {
  padding: var(--spacing-sm) 0;
  display: flex;
  justify-content: center;
}

.side-menu__logo-image,
.side-menu__logo-mark {
  width: auto;
  filter: brightness(0) invert(1);
}

.side-menu__logo-image {
  height: 40px;
}

/* The rail is too narrow for the wordmark, so it shows the mark on its own. */
.side-menu__logo-mark {
  display: none;
  height: 28px;
}

.side-menu--collapsed .side-menu__logo-image {
  display: none;
}

.side-menu--collapsed .side-menu__logo-mark {
  display: block;
}

/* The menu's item lists — global nav, the dossier nav, each Organisatiebeheer
   group and the bottom actions — share one row pitch, declared here once so it
   cannot diverge per list again. The cliëntrijen and the settings groups sat at
   `--spacing-2xs`, half the generic rows', and 2px is not legible as grouping,
   only as inconsistency; what marks either as a group is its header and the
   separator above it (#4377). A settings group's header sits inside the list, so
   this is also the gap below its title — the groups themselves are held apart by
   `__settings-context`'s own `--spacing-md`. */
.side-menu__nav,
.side-menu__bottom,
.side-menu__client-nav,
.side-menu__settings-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.side-menu__bottom {
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--color-overlay-on-dark);
  /* Never gives. It is the half of the sidebar that has to stay reachable. */
  flex-shrink: 0;
}

/* The one row pitch in the sidebar. Every item list reads it — the global nav,
   the dossier nav, each Organisatiebeheer group and the bottom actions — so the
   two halves of one sidebar read as peers rather than as a list and its
   sub-list. #4318 removed the type-size and icon-size differences between them
   for that reason and left 4px of vertical padding behind on `--context`, which
   is the one property an eye reads as hierarchy: the comment claimed peers while
   the CSS wrote a sub-list, and the two lists were reported as mismatched twice
   more before #4379 equalised them here.

   It equalises *down*, to the denser of the two, because the context navs are
   the long ones and this costs no vertical room. That puts the content box —
   `max(icon 20px, 16px x 1.5 line box)` = 24px — in a 40px row, which clears
   WCAG 2.2 SC 2.5.8 (AA, 24x24) but not the 44px of SC 2.5.5 (AAA) that touch
   guidance asks for. The block below is where that floor is paid, and it is
   deliberately not paid everywhere: see there. */
.side-menu__item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--radius-md);
  color: white;
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  transition: all var(--transition-duration-normal) var(--transition-easing-ease-out);
  cursor: pointer;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  font-size: var(--font-size-base);
  white-space: nowrap;
  overflow: hidden;
}

/* The 44px floor applies where a finger is the pointer, not everywhere. A width
   breakpoint is a proxy for input type and a poor one — a tablet in landscape at
   1200px still needs the larger target and a 900px window on a desktop with a
   mouse does not — so the question is asked directly. `any-pointer: coarse` is
   the more conservative spelling and the wrong one here: it matches any device
   with a touch input at all, so a touchscreen laptop driven by its trackpad
   would take the taller rows this change exists to avoid. One word to change if
   a practice reports otherwise. */
@media (pointer: coarse) {
  .side-menu__item {
    min-height: var(--sizing-touch-target-min);
  }
}

/* `--spacing-lg` on each side leaves less than an icon's width inside the
   collapsed rail, so the item's own horizontal padding has to give way there or
   the icon is clipped. This is also what keeps the rail's rows at `--spacing-md`
   — it outranks the base rule above for every item, `--context` included. The
   mobile drawer is wider and overrides it again. */
.side-menu--collapsed .side-menu__item {
  justify-content: center;
  padding: var(--spacing-md);
  position: relative;
}

.side-menu__item:hover {
  background: var(--color-overlay-light);
}

.side-menu__item--active {
  background: var(--color-overlay-on-dark);
  font-weight: var(--font-weight-semibold);
}

.side-menu__item--button {
  font-family: inherit;
}

.side-menu__collapse-toggle {
  margin-top: var(--spacing-sm);
  border-top: 1px solid var(--color-overlay-light);
  padding-top: var(--spacing-md);
}

.side-menu--collapsed .side-menu__collapse-toggle {
  border-top: none;
  padding-top: var(--spacing-md);
  margin-top: var(--spacing-sm);
}

.side-menu__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.side-menu__label {
  flex: 1;
}

.side-menu--collapsed .side-menu__label {
  display: none;
}

/* ==========================================================================
   Client dossier (epic #3144, #4256): collapsible "Hoofdmenu" group header +
   client-context block. All scoped to a dossier being open so the sidebar is
   unchanged everywhere else.
   ========================================================================== */
.side-menu__group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-lg);
  border: none;
  background: transparent;
  color: var(--color-overlay-on-dark-strong);
  cursor: pointer;
  font-family: inherit;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  width: 100%;
}

.side-menu__group-header:hover {
  color: white;
}

.side-menu__group-title {
  flex: 1;
  text-align: left;
}

.side-menu__group-chevron {
  flex-shrink: 0;
  transition: transform var(--transition-duration-normal) var(--transition-easing-ease-out);
}

.side-menu__group-chevron--collapsed {
  transform: rotate(-90deg);
}

.side-menu__client-context {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding-top: var(--spacing-md);
  margin-top: var(--spacing-xs);
  border-top: 1px solid var(--color-overlay-on-dark);
}

.side-menu__client-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 0 var(--spacing-sm);
  min-width: 0;
}

.side-menu__client-heading {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.side-menu__client-name {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.side-menu__client-subtitle {
  font-size: var(--font-size-xs);
  color: var(--color-overlay-on-dark-strong);
}

/* `side-menu__item--context` — the row of whichever context is open, a
   dossier's or Organisatiebeheer's; it was `--client` until the settings
   sidebar (#4368) generalised it — deliberately declares no box of its own.
   Type size, icon size and now vertical padding all come from
   `.side-menu__item`, which is what makes the two halves of the sidebar peers
   (#4318, #4379). The class stays as a selector rather than a style: the
   template keys `--active` off it and both `SideMenu.spec.ts` and
   `e2e/tests/dossier-sidebar.spec.ts` find rows by it.

   Do not give it a denser pitch again to win back vertical room. That is the
   defect this closed, spelled as a fix — #4372's scroll region is where a long
   context nav is paid for. */

/* Hide the mini header labels in the icon-only rail. */
.side-menu--collapsed .side-menu__client-header {
  justify-content: center;
}

.side-menu--collapsed .side-menu__client-heading {
  display: none;
}

/* ==========================================================================
   Organisatiebeheer (#4368): the settings-context block. Same rows as the
   dossier's, plus a heading per group — the structure the hub already renders.
   ========================================================================== */
.side-menu__settings-context {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding-top: var(--spacing-md);
  margin-top: var(--spacing-xs);
  border-top: 1px solid var(--color-overlay-on-dark);
}

.side-menu__context-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 0 var(--spacing-sm);
  min-width: 0;
}

.side-menu__context-title {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.side-menu__settings-group-title {
  padding: 0 var(--spacing-lg);
  color: var(--color-overlay-on-dark-strong);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* The collapsed rail is icons only, so a heading with nowhere to go would just
   be a stripe of clipped text — the same call the dossier's mini header makes. */
.side-menu--collapsed .side-menu__settings-group-title,
.side-menu--collapsed .side-menu__context-title {
  display: none;
}

.side-menu--collapsed .side-menu__context-header {
  justify-content: center;
}

/* Mobile styles - disable collapsed mode on tablet/mobile */
@media (--breakpoint-lg-down) {
  /* Below `--breakpoint-lg-up` the settings sidebar is not offered at all and
     the hub stays the navigation: twenty rows in the mobile drawer would bury
     the global nav under a screen the user reached from the header. */
  .side-menu__settings-context {
    display: none;
  }

  .side-menu--collapsed {
    width: var(--sidebar-width-expanded);
    padding: var(--spacing-lg);
  }

  /* The drawer is a full-width labelled menu, so its rows are ordinary rows
     again: this undoes the rail's square `--spacing-md`, and it restores the
     base pitch rather than restating one — collapsed and expanded are the same
     menu at this width, and a row that changed height when the toggle was
     pressed would be the #4379 mismatch one state over. */
  .side-menu--collapsed .side-menu__item {
    justify-content: flex-start;
    padding: var(--spacing-sm) var(--spacing-lg);
  }

  .side-menu--collapsed .side-menu__logo {
    padding: var(--spacing-md) 0;
  }

  .side-menu--collapsed .side-menu__logo-image {
    display: block;
  }

  .side-menu--collapsed .side-menu__logo-mark {
    display: none;
  }

  /* Show labels on mobile even when collapsed — in a dossier too. The drawer is
     opened from the hamburger to be read, and an icon-only rail there left a
     practitioner with no labelled way between dossier sections. */
  .side-menu--collapsed .side-menu__label {
    display: block;
  }

  .side-menu--collapsed .side-menu__client-header {
    justify-content: flex-start;
    padding: 0 var(--spacing-sm);
  }

  .side-menu--collapsed .side-menu__client-heading {
    display: flex;
  }

  /* Hide the collapse toggle in the mobile drawer: the drawer slides in at full
     width with its labels showing (the rules above), so there is nothing for the
     toggle to reveal and pressing it would change nothing visible. */
  .side-menu__collapse-toggle {
    display: none;
  }
}

@media (--breakpoint-xxs-down) {
  .side-menu {
    width: 220px;
    padding: var(--spacing-sm);
  }

  .side-menu--collapsed {
    width: 220px;
    padding: var(--spacing-sm);
  }

  .side-menu__item {
    padding: var(--spacing-sm) var(--spacing-md);
    gap: var(--spacing-sm);
    font-size: var(--font-size-sm);
  }

  .side-menu--collapsed .side-menu__item {
    padding: var(--spacing-sm) var(--spacing-md);
  }
}
</style>
